import { createError, useNuxtApp } from '#imports'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

import { useSession } from './useSession'
import { useUserStore } from './useUserStore'

type FallbackRoute = Exclude<RouteLocationRaw, string>
type MiddlewareReturn = Exclude<RouteLocationRaw, string> | undefined

/**
 * Guest magic-link flow — token exchange and session establishment.
 *
 * Used by the global `auth` middleware for routes whose `meta.auth.mode` is
 * `'guest-magic-link'`. The fallback route to redirect to when the flow fails
 * is passed in from `meta.auth.redirection`.
 *
 * The route is expected to carry both a `token` (the magic-link access token
 * exchanged at `/auth/gateway`) and a `bm_user_id` (the user identifier, kept
 * and forwarded to the fallback route on any redirect).
 *
 * - If a `token` is present, it is sent to `/auth/gateway`. If the token is
 *   valid (the request resolves), the session is established and the page is
 *   shown after the `token` is stripped from the URL. If the token is expired
 *   or invalid (the request rejects), the user is redirected to the fallback
 *   route with `bm_user_id` forwarded.
 * - If no `token` is present, the current guest status is checked. If the user
 *   is already authenticated as a guest, the page is shown. Otherwise, the user
 *   is redirected to the fallback route with `bm_user_id` forwarded.
 */
export function useGuestMagicLink() {
  const nuxtApp = useNuxtApp()
  const { isUserFetched, isUserGuest, fetchUser, user } = useUserStore()
  const { getSessionCookie } = useSession()

  async function handleGuestMagicLink(
    route: RouteLocationNormalized,
    fallbackRoute: FallbackRoute,
  ): Promise<MiddlewareReturn> {
    const { token: tokenQuery, ...queryWithoutToken } = route.query
    const token = Array.isArray(tokenQuery) ? tokenQuery[0] : tokenQuery
    const bmUserIdQuery = route.query.bm_user_id
    const bmUserId = Array.isArray(bmUserIdQuery)
      ? bmUserIdQuery[0]
      : bmUserIdQuery

    function redirectToFallback(): MiddlewareReturn {
      if (!bmUserId) {
        throw createError({
          statusCode: 422,
          statusMessage: 'Missing bm_user_id for guest magic link',
        })
      }

      return {
        ...fallbackRoute,
        query: { ...fallbackRoute.query, bm_user_id: bmUserId },
      }
    }

    // A token is present in the URL: exchange it for a session via /auth/gateway.
    if (token) {
      try {
        await getSessionCookie(token)
        await nuxtApp.runWithContext(() => fetchUser())
      } catch {
        // The token is expired or invalid: send the user to the fallback route,
        // forwarding the bm_user_id so the destination knows which user to re-authenticate.
        return redirectToFallback()
      }

      // The token was valid. The navigation re-runs this middleware without
      // the token, which now resolves through the guest session established above.
      // Ensure bm_user_id is present in the URL so downstream pages can use it.
      return {
        path: route.path,
        query: {
          ...queryWithoutToken,
          bm_user_id: queryWithoutToken.bm_user_id ?? user.value.bmUserId,
        },
        hash: route.hash,
      }
    }

    // No token in the URL: only let guests through.
    if (!isUserFetched.value) {
      await nuxtApp.runWithContext(() => fetchUser())
    }

    if (isUserGuest.value) return undefined

    return redirectToFallback()
  }

  return { handleGuestMagicLink }
}
